import { useEffect, useState } from 'react';

import { initials } from '@/lib/initials';
import { teamColor } from '@/lib/team-colors';
import { cn } from '@/lib/utils';

/**
 * Iniciálék a névből: első + utolsó szó kezdőBETŰJE (egy szónál az első kettő).
 *
 * ⚠️ CSAK BETŰ/SZÁMJEGY kerülhet a monogramba. A nyers „első karakter" írásjelet is beengedett: a gépi
 * `notify-service (atlas)` avatarja **„N("** lett (mérve az eseménynaplón), mert a második szó a nyitó
 * zárójellel kezdődik. Ugyanez ütne minden zárójeles/idézőjeles néven. A szűrés `\p{L}\p{N}` unicode-
 * osztályon megy, tehát az ékezetes betűk (Árpád → Á) megmaradnak.
 *
 * Betű nélküli szó (pl. „(…)", „—") KIMARAD a választásból; ha egyetlen betű sincs, a jel `?`.
 */
// A design terv 10 színű, determinisztikus avatar-palettája (névből hashelve — stabil szín).
const AVATAR_COLORS = [
  '#5b5bf0',
  '#2a8ad6',
  '#1ea672',
  '#d39822',
  '#d8434f',
  '#8a4fd9',
  '#0d4a42',
  '#38adbd',
  '#e07a3a',
  '#3d35c2',
] as const;

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const SIZES = {
  sm: 'size-[22px] text-[9.5px]',
  md: 'size-7 text-xs',
  lg: 'size-9 text-md',
  xl: 'size-14 text-lg',
} as const;

/**
 * Avatar monogram-fallbackkel (a design terv szerint). Kép-forrás sorrend:
 *   1) explicit `src` prop (presigned objektum-tár-URL, ha van feltöltött profilkép), különben
 *   2) determinisztikus, 10 színű iniciálé-monogram (a `name` kezdőbetűiből).
 * Ha a kép nem tölt be (lejárt/hibás presigned URL), az `onError` szintén a monogramra esik vissza.
 * A névből-slug származtatás (régi `public/avatars/<slug>.webp` konvenció) KIVEZETVE — a profilképet
 * a backend adja presigned URL-ként. Méretek: sm 22 / md 28 / lg 36 / xl 56 px.
 */
export function Avatar({
  name,
  src,
  size = 'md',
  accentColor,
  className,
}: {
  name: string;
  /** Explicit kép-URL (presigned). `null`/`undefined` → nincs kép (monogram fallback). */
  src?: string | null;
  size?: keyof typeof SIZES;
  /**
   * Opcionális csapat-szín TOKEN (MORGENS-paletta, pl. `grove`). Ha megadva: színes GYŰRŰ a fotó körül, és a
   * monogram-fallback HÁTTERE is ez a szín (a determinisztikus hash helyett) — így a csapat vizuálisan mindig
   * jelen van. A globális Avatar bárhol újrahasználható ezzel (címtár, navbar, kártya).
   */
  accentColor?: string | null | undefined;
  className?: string;
}): JSX.Element {
  const [failed, setFailed] = useState(false);
  // Új forrás → a hiba-állapot nullázása (lista-újrarenderelésnél a sor eltérő képet kaphat).
  useEffect(() => setFailed(false), [src]);

  const accent = accentColor ? teamColor(accentColor) : null;

  const base = cn(
    'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold tracking-[0.01em] text-white',
    SIZES[size],
    className,
  );

  if (src && !failed) {
    // A profilképek ÁTLÁTSZÓ hátterű webp-k → a kör HÁTTERE a csapat-szín (az átlátszó részeken átsüt). Nincs gyűrű.
    return (
      <span className={base} style={accent ? { backgroundColor: accent.bg } : undefined}>
        <img
          src={src}
          alt={name}
          className="size-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      className={base}
      style={{
        backgroundColor: accent ? accent.bg : AVATAR_COLORS[hashStr(name) % AVATAR_COLORS.length],
        ...(accent ? { color: accent.text } : {}),
      }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
